import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button, Skeleton } from '@/components/ui';
import type { OrgAdminRow, Organization, OrgTenant, ProvisioningStatus } from '@/api/orgAdmin';
import { OrgAvatar } from './OrgAvatar';
import styles from './OrgDetail.module.css';

interface BlockingItem {
    key: string;
    text: string;
}

// EAA-1845: takes `t` explicitly since this is a plain helper called from the
// component body, not a component itself — it cannot call the
// `useTranslation` hook on its own.
function blockingItems(coverage: ProvisioningStatus, t: TFunction): BlockingItem[] {
    const items: BlockingItem[] = [];
    if (coverage.assignments.error > 0) {
        items.push({
            key: 'assignment-error',
            text: t('admin.organizations.overview.assignmentErrorText', {
                count: coverage.assignments.error,
            }),
        });
    }
    if (coverage.orphan_levels > 0) {
        items.push({
            key: 'orphan-levels',
            text: t('admin.organizations.overview.orphanLevelsText', {
                count: coverage.orphan_levels,
            }),
        });
    }
    return items;
}

interface OrgOverviewTabProps {
    org: Organization;
    tenants: OrgTenant[];
    tenantsLoading: boolean;
    admins: OrgAdminRow[];
    adminsLoading: boolean;
    coverage: ProvisioningStatus | undefined;
    coverageLoading: boolean;
    isSuperAdmin: boolean;
    canManage: boolean;
    onAddTenant: () => void;
    onGoToTenants: () => void;
    onGoToAdmins: () => void;
    onGoToBranding: () => void;
    onViewHierarchy: () => void;
    onToggleOrgActive: () => void;
    toggling: boolean;
}

/**
 * EAA-1815: `/organizations/:id` — "Tổng quan" tab, frame 22's main body
 * (lines 155-599). "Leads with what's broken": the blocking-issues card is
 * built from `GET /organizations/{id}/provisioning-status` — the same real
 * endpoint `HierarchyPage`'s coverage card reads — and is absent entirely
 * when `coverage.status !== 'attention'`, never rendered empty/green.
 *
 * The Cơ sở preview's "Cấp cha" column always reads the organization's own
 * name: `OrgTenant` carries no `parent_tenant_id` (confirmed against
 * `src/api/orgAdmin.ts`), so tenant-under-tenant nesting from the design
 * frame (a tenant like "Khối 1–5" parented under another tenant) has no real
 * data behind it today — only the flat org→tenant relationship the D16 model
 * actually has. See the ticket report for this deviation.
 */
export function OrgOverviewTab({
    org,
    tenants,
    tenantsLoading,
    admins,
    adminsLoading,
    coverage,
    coverageLoading,
    isSuperAdmin,
    canManage,
    onAddTenant,
    onGoToTenants,
    onGoToAdmins,
    onGoToBranding,
    onViewHierarchy,
    onToggleOrgActive,
    toggling,
}: OrgOverviewTabProps) {
    const { t } = useTranslation();
    const blocking =
        coverage && coverage.status === 'attention' ? blockingItems(coverage, t) : [];
    const brandColor = org.branding?.primary_color;
    const accountsActive = coverage?.assignments.active;
    // EAA-1846: the cơ sở table's cell chrome used to come from
    // `.portal-org-tenant-table td`, a container rule reaching a bare element — it
    // restyled ANY cell dropped into the table, kit components included. Each cell
    // names its own classes now; these two pairs are hoisted so the JSX below stays
    // inside the 100-column width.
    const tenantNameCell = `${styles.tenantCell} ${styles.tenantName}`;
    const tenantParentCell = `${styles.tenantCell} ${styles.tenantParent}`;

    return (
        <div className={styles.body}>
            <div className={styles.main}>
                {coverageLoading && (
                    <div role="status" aria-live="polite">
                        <span className="portal-sr-only">
                            {t('admin.organizations.overview.loadingCoverageSrOnly')}
                        </span>
                        <Skeleton height="4rem" />
                    </div>
                )}

                {blocking.length > 0 && (
                    <div className={styles.blockingCard} role="alert">
                        <span className={styles.blockingHead}>
                            <span className={styles.blockingIcon}>!</span>
                            <span className={styles.blockingTitle}>
                                {t('admin.organizations.overview.blockingTitle', {
                                    count: blocking.length,
                                })}
                            </span>
                        </span>
                        {blocking.map((item) => (
                            <span className={styles.blockingItem} key={item.key}>
                                <span className={styles.blockingItemText}>{item.text}</span>
                                <button
                                    type="button"
                                    className={styles.blockingItemAction}
                                    onClick={onViewHierarchy}
                                >
                                    {t('admin.organizations.overview.blockingItemAction')}
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                <div className={styles.tenantTableCard}>
                    <span className={styles.tenantTableTitle}>
                        {t('admin.organizations.tenantsLabel')}
                    </span>
                    {tenantsLoading && (
                        <div role="status" aria-live="polite">
                            <span className="portal-sr-only">
                                {t('admin.organizations.loadingSrOnly')}
                            </span>
                            <Skeleton height="6rem" />
                        </div>
                    )}
                    {!tenantsLoading && tenants.length === 0 && (
                        <p className={styles.noTenantCallout}>
                            {t('admin.organizations.overview.noTenantCallout')}
                            {canManage && (
                                <button
                                    type="button"
                                    className={styles.blockingItemAction}
                                    onClick={onAddTenant}
                                >
                                    {t('admin.organizations.createTenantAction')}
                                </button>
                            )}
                        </p>
                    )}
                    {!tenantsLoading && tenants.length > 0 && (
                        <table className={styles.tenantTable}>
                            <thead>
                                <tr>
                                    <th className={styles.tenantHeadCell}>
                                        {t('admin.organizations.columnName')}
                                    </th>
                                    <th className={styles.tenantHeadCell}>
                                        {t('admin.organizations.columnParentLevel')}
                                    </th>
                                    <th className={styles.tenantHeadCell}>
                                        {t('admin.organizations.columnStatus')}
                                    </th>
                                    <th className={styles.tenantHeadCell} />
                                </tr>
                            </thead>
                            <tbody>
                                {tenants.slice(0, 6).map((tenant) => (
                                    <tr key={tenant.id}>
                                        <td className={tenantNameCell}>
                                            {tenant.name ?? tenant.tenant_id}
                                        </td>
                                        <td className={tenantParentCell}>{org.name}</td>
                                        <td className={styles.tenantCell}>
                                            <span
                                                className={
                                                    'portal-console-badge' +
                                                    (tenant.tenant_is_active ? ' is-on' : '')
                                                }
                                            >
                                                {tenant.tenant_is_active
                                                    ? t('admin.organizations.status.active')
                                                    : t('admin.organizations.statusTabs.inactive')}
                                            </span>
                                        </td>
                                        <td
                                            className={styles.tenantCell}
                                            style={{
                                                textAlign: 'right',
                                                color: 'var(--color-text-muted)',
                                            }}
                                        >
                                            ⋯
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {tenants.length > 6 && (
                        <button
                            type="button"
                            className={styles.asideLink}
                            onClick={onGoToTenants}
                        >
                            {t('admin.organizations.overview.viewAllTenants', {
                                count: tenants.length,
                            })}
                        </button>
                    )}
                </div>
            </div>

            <div className={styles.aside}>
                <div className={styles.asideCard}>
                    <span className={styles.asideCardTitle}>
                        {t('admin.organizations.detail.tabs.admins')}
                    </span>
                    {adminsLoading && (
                        <div role="status" aria-live="polite">
                            <span className="portal-sr-only">
                                {t('admin.organizations.loadingSrOnly')}
                            </span>
                            <Skeleton height="2rem" />
                        </div>
                    )}
                    {!adminsLoading &&
                        admins.slice(0, 3).map((admin) => (
                            <span className={styles.adminRow} key={admin.id}>
                                <OrgAvatar
                                    name={admin.user_email || admin.user_sub}
                                    size={28}
                                    radius={999}
                                    tone="accent"
                                    fontSize={11}
                                />
                                <span className={styles.adminRowIdentity}>
                                    <span className={styles.adminRowName}>
                                        {admin.user_email || admin.user_sub}
                                    </span>
                                </span>
                            </span>
                        ))}
                    {!adminsLoading && admins.length === 0 && (
                        <span className={styles.asideHint}>
                            {t('admin.organizations.overview.noAdminsHint')}
                        </span>
                    )}
                    {isSuperAdmin && (
                        <button type="button" className={styles.asideLink} onClick={onGoToAdmins}>
                            {t('admin.organizations.overview.addAdminLink')}
                        </button>
                    )}
                </div>

                <div className={styles.asideCard}>
                    <span className={styles.asideCardTitle}>
                        {t('admin.organizations.detail.tabs.branding')}
                    </span>
                    <span className={styles.brandRow}>
                        <span
                            className={styles.brandSwatch}
                            style={{ background: brandColor || 'var(--color-primary)' }}
                            aria-hidden="true"
                        />
                        <code className={styles.brandCode}>
                            {brandColor
                                ? brandColor.toUpperCase()
                                : t('admin.organizations.defaultBrandLabel')}
                        </code>
                        {canManage && (
                            <button
                                type="button"
                                className={styles.asideLink}
                                onClick={onGoToBranding}
                            >
                                {t('common.edit')}
                            </button>
                        )}
                    </span>
                    <span className={styles.asideHint}>
                        {t('admin.organizations.overview.brandingHint')}
                    </span>
                </div>

                {isSuperAdmin && (
                    <div className={styles.asideCard}>
                        <span className={styles.asideCardTitle}>
                            {t('admin.organizations.overview.dangerCardTitle')}
                        </span>
                        <span className={styles.asideHint}>
                            {org.is_active
                                ? t('admin.organizations.overview.dangerHintActive', {
                                      value:
                                          accountsActive === undefined
                                              ? t('admin.organizations.overview.allAccountsWord')
                                              : accountsActive,
                                  })
                                : t('admin.organizations.overview.dangerHintInactive')}
                        </span>
                        {/* EAA-1804: the last raw `portal-btn` in the app, and the only one whose
                            class was dynamic — `portal-org-danger-btn` when active, plain
                            `portal-btn` when not. That is antd's `danger` boolean, so the state
                            now rides a prop instead of a class swap. The bespoke class is dropped
                            rather than kept: it hardcoded height 32 / an inset error ring /
                            transparent bg, which would have fought antd's own box model (nothing
                            scopes it with `:not(.ant-btn)`). `size="small"` is the kit's nearest
                            token (34) — a 2px difference, and the kit is authoritative now. */}
                        <Button
                            danger={org.is_active}
                            size="small"
                            onClick={onToggleOrgActive}
                            disabled={toggling}
                        >
                            {toggling
                                ? t('admin.organizations.processingLabel')
                                : org.is_active
                                  ? t('admin.organizations.deactivateAction')
                                  : t('admin.organizations.reactivateAction')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default OrgOverviewTab;
